import {getServiceBranchFileNames, getBranchFileNames, getCarrierFileNames} from 'config/MenuFileNames'
import _ from 'lodash'

/**
 * This function maps the security parameters to a specific index
 * based on the service and branch related rights info.
 *
 * @param {object} securityParams - The security parameters object.
 * @return {string} The corresponding filename from the service branch user file names array.
 */
const getServiceUserFileName = (securityParams: object): string => {
    // Check if the user is a branch member
    const branchMemberStatus: boolean = _.get(securityParams, 'memberStatus', false)
    // Check if the user is a branch administrator
    const branchAdminStatus: boolean = _.get(securityParams, 'serviceAdmin', false)
    // Check if the user is a co-loader
    const coLoaderStatus: boolean = _.get(securityParams, 'coLoader', false)
    // Check if the user has booking authorization
    const bookingAuthorization: boolean = _.get(securityParams, 'bookingEnabled', false)
    // Check if the user has rate authorization
    const rateAuthorization: boolean = _.get(securityParams, 'ratingEnabled', false)
    // Check if the user has allocation authorization
    const allocationAuthorization: boolean = _.get(securityParams, 'allosEnabled', false)

    // Get the branch menu file names array
    const serviceBranchMenuFileNames: string[] = getServiceBranchFileNames()
    // Calculate the index based on the user's rights
    const index =
        (branchMemberStatus ? 1 : 0) |
        (branchAdminStatus ? 1 << 1 : 0) |
        (coLoaderStatus ? 1 << 2 : 0) |
        (bookingAuthorization ? 1 << 3 : 0) |
        (rateAuthorization ? 1 << 4 : 0) |
        (allocationAuthorization ? 1 << 5 : 0)
    // Return the corresponding filename from the branch menu file names array
    return serviceBranchMenuFileNames[index]
}

/**
 * This function maps the security parameters to a specific index
 * based on the branch related rights info.
 *
 * @param {object} securityParams - The security parameters object.
 * @return {string} The corresponding filename from the branch file names array.
 */
const getBranchUserFileName = (securityParams: object): string => {
    // Check if the user is a branch member
    const branchMemberStatus: boolean = _.get(securityParams, 'memberStatus', false)
    // Check if the user is a branch administrator
    const branchAdminStatus: boolean = _.get(securityParams, 'branchAdmin', false)
    // Check if the user is a co-loader
    const coLoaderStatus: boolean = _.get(securityParams, 'coLoader', false)
    // Check if the user has booking authorization
    const bookingAuthorization: boolean = _.get(securityParams, 'bookingEnabled', false)
    // Check if the user has rate authorization
    const rateAuthorization: boolean = _.get(securityParams, 'ratingEnabled', false)
    // Check if the user has allocation authorization
    const allocationAuthorization: boolean = _.get(securityParams, 'allosEnabled', false)

    // Get the branch menu file names array
    const branchMenuFileNames: string[] = getBranchFileNames()
    // Calculate the index based on the user's rights
    const index =
        (branchMemberStatus ? 1 : 0) |
        (branchAdminStatus ? 1 << 1 : 0) |
        (coLoaderStatus ? 1 << 2 : 0) |
        (bookingAuthorization ? 1 << 3 : 0) |
        (rateAuthorization ? 1 << 4 : 0) |
        (allocationAuthorization ? 1 << 5 : 0)
    // Return the corresponding filename from the branch menu file names array
    return branchMenuFileNames[index]
}

/**
 * This function maps the security parameters to a specific index
 * based on the carrier related rights info.
 *
 * @param {object} securityParams - The security parameters object.
 * @return {string} The corresponding filename from the carrier menu file names array.
 */
const getCarrierUserFileName = (securityParams: object): string => {
    // Carrier related rights info
    // Check if the user is a carrier admin
    const carrierAdminStatus: boolean = _.get(securityParams, 'carrierAdmin', false)
    // Check if the user is a second level carrier admin
    const carrierAdminLevel: boolean = _.get(securityParams, 'secondLevelCarrierAdmin', false)
    // Check if the user has GSA (Global Service Agreement) authorization
    const carrierGSAStatus: boolean = _.get(securityParams, 'gsaenabled', false)
    // Check if the user has rate authorization
    const rateAuthorization: boolean = _.get(securityParams, 'ratingEnabled', false)
    // Check if the user has allocation authorization
    const allocationAuthorization: boolean = _.get(securityParams, 'allosEnabled', false)

    const carrierMenuFileNames: string[] = getCarrierFileNames()
    // Calculate the index based on the user's rights
    const index =
        (carrierAdminStatus ? 1 : 0) |
        (carrierAdminLevel ? 1 << 1 : 0) |
        (carrierGSAStatus ? 1 << 2 : 0) |
        (allocationAuthorization ? 1 << 3 : 0) |
        (rateAuthorization ? 1 << 4 : 0)
    // Return the corresponding filename from the carrier menu file names array
    return carrierMenuFileNames[index]
}

/**
 * This function constructs the branch menus based on the login details and the menu groups.
 * It filters out the menu items that are not allowed for the user based on the associated carriers.
 * It also filters out the menu items that are not allowed for the user based on the branch ID.
 *
 * @param {Array} menuGroups - The menu groups to be constructed.
 * @param {Object} login - The login object containing the login details.
 * @return {Array} The constructed menu groups.
 */
const constructBranchMenus = (menuGroups: object[], login: object, isServiceUser: boolean = false): object[] => {
    // Iterate over each menu group
    menuGroups.forEach((menu: any) => {
        // Get the associated carriers from the login details
        const approvedCarriers: string[] = _.get(login, 'loginDetails.associatedCarriers', [])
        let subMenus = _.get(menu, 'subMenus', [])

        // Check if the menu group is for offers or eClaims
        if (menu.authPrivilege === 'OfferMenu' || menu.authPrivilege === 'eClaims') {
            // Filter out the menu items that are not allowed for the user based on the associated carriers
            subMenus = subMenus.filter(
                (menuItem: any) => menuItem.carrier === undefined || approvedCarriers.includes(menuItem.carrier)
            )
            menu.subMenus = subMenus
        } else if (menu.authPrivilege === 'administration') {
            // Get the branch ID from the login details
            const branchId = _.get(login, 'loginDetails.securityParams.branchID', -1)

            let userManagementMenus = _.get(menu, 'userManagementMenus', [])
            userManagementMenus = userManagementMenus.filter((menuItem: any) => menuItem.active && !menuItem.disable)

            let tools = _.get(menu, 'tools', [])
            if (isServiceUser) {
                // Filter out the menu items that are not allowed for the user based on the branch ID
                tools = tools.filter((menuItem: any) => menuItem.active && !menuItem.disable)
            } else {
                // Filter out the menu items that are not allowed for the user based on the branch ID
                tools = tools.filter(
                    (menuItem: any) => menuItem.allowedBranches?.includes(branchId.toString()) || false
                )
            }

            menu.userManagementMenus = userManagementMenus
            menu.tools = tools
        }
    })
    return menuGroups
}

/**
 * This function constructs the carrier menus based on the security parameters and the menu groups.
 * It filters out the menu items that are not allowed for the user based on the promotion status and the carrier code.
 * It also filters out the menu items that are not allowed for the user based on the disabled carriers.
 *
 * @param {Array} menuGroups - The menu groups to be constructed.
 * @param {Object} securityParams - The security parameters containing the promotion status and the carrier code.
 * @return {Array} The constructed menu groups.
 */
const constructCarrierMenus = (menuGroups: object[], securityParams: object): object[] => {
    menuGroups.forEach((menu: any) => {
        const offerStatus = _.get(securityParams, 'promotionEnabled', false)
        const carrierCode = _.get(securityParams, 'carrierCode', 'CPS')
        let subMenus = _.get(menu, 'subMenus', [])
        if (menu.authPrivilege === 'OfferMenu') {
            // eslint-disable-next-line no-template-curly-in-string
            const carrierReplaceTemplate = '${carrier}'
            if (offerStatus) {
                subMenus.forEach((menuItem: any) => {
                    // Replace the carrier code in the title, pathname, and authPrivilege
                    menuItem.title = menuItem.title.replace(carrierReplaceTemplate, carrierCode)
                    menuItem.pathname = menuItem.pathname.replace(carrierReplaceTemplate, carrierCode)
                    menuItem.authPrivilege = menuItem.authPrivilege.replace(carrierReplaceTemplate, carrierCode)
                })
            } else {
                subMenus = [] // Clear the subMenus array if promotion status is false
                menu.disable = true // Disable the menu if promotion status is false
            }
            menu.subMenus = subMenus
        } else if (menu.authPrivilege === 'administration') {
            let userManagementMenus = _.get(menu, 'userManagementMenus', [])
            userManagementMenus = userManagementMenus.filter((menuItem: any) => menuItem.active && !menuItem.disable)

            let tools = _.get(menu, 'tools', [])
            tools = tools.filter((menuItem: any) => menuItem.active)
            tools.forEach((menuItem: any) => {
                if (menuItem.title === 'Manage Offers') {
                    menuItem.disable = !offerStatus
                } else {
                    // Disable the menu item if the carrier code is in the disabled carriers list
                    menuItem.disable = menuItem.disabledCarriers?.includes(carrierCode) || false
                }
            })

            menu.userManagementMenus = userManagementMenus
            menu.tools = tools
        }
    })
    return menuGroups
}

export {
    getServiceUserFileName,
    getBranchUserFileName,
    getCarrierUserFileName,
    constructBranchMenus,
    constructCarrierMenus
}
